'use client';

import React, { type SyntheticEvent } from 'react';
import ReactCrop, { centerCrop, makeAspectCrop, type Crop, type PixelCrop } from 'react-image-crop';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';

import 'react-image-crop/dist/ReactCrop.css';
import { CropIcon, Trash2Icon, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { getCroppedImg } from '@/lib/canvas-utils';
import { UNSUPPORTED_IMAGE_FORMAT_MESSAGE } from '@/lib/prepare-image-file';
import { cn } from '@/lib/utils';

interface ImageCropperProps {
  imageSrc: string | null;
  onCropComplete: (croppedImageBlob: Blob) => Promise<void>;
  onCancel: () => void;
  aspect?: number;
  useDialog?: boolean;
}

export function ImageCropper({
  imageSrc,
  onCropComplete,
  onCancel,
  aspect = 1,
  useDialog = true,
}: ImageCropperProps) {
  const imgRef = React.useRef<HTMLImageElement | null>(null);

  const [crop, setCrop] = React.useState<Crop>();
  const [completedCrop, setCompletedCrop] = React.useState<PixelCrop>();
  const [loading, setLoading] = React.useState(false);
  const [postCropPhase, setPostCropPhase] = React.useState(false);

  function onImageLoad(e: SyntheticEvent<HTMLImageElement>) {
    if (aspect) {
      const { width, height } = e.currentTarget;
      setCrop(centerAspectCrop(width, height, aspect));
    }
  }

  async function onCrop() {
    if (!imgRef.current || !completedCrop) return;

    setLoading(true);
    setPostCropPhase(false);
    try {
      const croppedBlob = await getCroppedImg(imgRef.current, completedCrop);
      if (croppedBlob) {
        setPostCropPhase(true);
        await onCropComplete(croppedBlob);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setPostCropPhase(false);
      setLoading(false);
    }
  }

  async function handleCrop(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    await onCrop();
  }

  const fittedImageMaxHeight = useDialog
    ? 'max-h-[min(calc(100dvh-16rem),560px)]'
    : 'max-h-[min(50dvh,420px)]';

  const cropViewport = (
    <div
      className={cn(
        'flex min-h-0 flex-1 items-center justify-center overflow-hidden bg-black/5 px-4 py-4 sm:px-6 sm:py-5',
        !useDialog && 'rounded-2xl border border-border/10 shadow-inner'
      )}
      onClick={(e) => e.stopPropagation()}
    >
      <ReactCrop
        crop={crop}
        onChange={(_, percentCrop) => setCrop(percentCrop)}
        onComplete={(c) => setCompletedCrop(c)}
        aspect={aspect}
        className={cn('max-w-full', fittedImageMaxHeight)}
      >
        <img
          ref={imgRef}
          alt="Crop preview"
          src={imageSrc || ''}
          onLoad={onImageLoad}
          onError={() => {
            toast.error(UNSUPPORTED_IMAGE_FORMAT_MESSAGE);
            onCancel();
          }}
          className={cn(
            'mx-auto block h-auto w-auto max-w-full object-contain object-center transition-all duration-300',
            fittedImageMaxHeight
          )}
        />
      </ReactCrop>
    </div>
  );

  const cropActions = (
    <div
      className={cn(
        'flex shrink-0 items-center justify-between gap-4 border-t px-4 py-4 sm:px-6 sm:py-5',
        useDialog ? 'border-border bg-muted/50' : ''
      )}
    >
      <Button
        type="button"
        size={'sm'}
        variant={'ghost'}
        onClick={onCancel}
        disabled={loading}
        className="text-muted-foreground hover:text-foreground hover:bg-destructive/10 transition-colors"
      >
        <Trash2Icon className="mr-2 size-4" />
        Cancel
      </Button>
      <Button
        type="button"
        size={'sm'}
        onClick={handleCrop}
        disabled={loading || !completedCrop}
        className="shadow-md hover:scale-105 active:scale-95 transition-all"
      >
        {loading ? (
          <Loader2 className="mr-2 size-4 animate-spin" />
        ) : (
          <CropIcon className="mr-2 size-4" />
        )}
        {loading ? (postCropPhase ? 'Uploading image…' : 'Processing…') : 'Apply & Save'}
      </Button>
    </div>
  );

  if (!useDialog) {
    return (
      <div className="grid min-h-[min(50dvh,420px)] w-full animate-in fade-in zoom-in-95 grid-rows-[minmax(0,1fr)_auto] overflow-hidden duration-300">
        {cropViewport}
        {cropActions}
      </div>
    );
  }

  return (
    <Dialog open={!!imageSrc} onOpenChange={(open) => !open && onCancel()}>
      <DialogContent
        className="grid max-h-[calc(100dvh-2rem)] w-[calc(100vw-1rem)] max-w-2xl grid-rows-[auto_minmax(0,1fr)_auto] gap-0 overflow-hidden border-border/50 bg-background/95 p-0 shadow-2xl backdrop-blur-xl"
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader className="shrink-0 border-b border-border/10 bg-muted/50 p-4 text-left sm:p-6">
          <DialogTitle className="text-lg font-bold tracking-tight text-foreground">
            Adjust your Photo
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            {postCropPhase
              ? 'Uploading your cropped image — keep this window open.'
              : 'Drag the crop box to choose the area you want to keep.'}
          </DialogDescription>
        </DialogHeader>
        {cropViewport}
        {cropActions}
      </DialogContent>
    </Dialog>
  );
}

export function centerAspectCrop(mediaWidth: number, mediaHeight: number, aspect: number) {
  const mediaAspect = mediaWidth / mediaHeight;
  const cropConfig =
    mediaAspect > aspect ? { unit: '%' as const, height: 90 } : { unit: '%' as const, width: 90 };

  return centerCrop(
    makeAspectCrop(cropConfig, aspect, mediaWidth, mediaHeight),
    mediaWidth,
    mediaHeight
  );
}
